/**
 * Tabela de dados, largura total. Extrai o padrão visual de `Usuarios.tsx` (cabeçalho
 * em `tipo-rotulo`, linhas com hover, borda entre linhas) SEM a edição inline — que é
 * justamente o que estamos abandonando: editar é uma tela cheia, não uma linha que
 * incha. A linha inteira é clicável (leva à edição); daí o `role="button"`.
 */
import type * as React from 'react';
import { cn } from '@/lib/utils';

export function Tabela({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full', className)}>{children}</table>
    </div>
  );
}

/** O cabeçalho ganha fundo próprio e fica GRUDADO no topo: numa lista de 50 linhas, saber de que
 *  coluna é o dado que você está lendo no meio da rolagem não pode depender de rolar de volta. */
export function Thead({ children }: { children: React.ReactNode }) {
  return (
    <thead className="sticky top-0 z-[1] bg-surface-2">
      <tr className="border-b border-border-strong text-left">{children}</tr>
    </thead>
  );
}

export function Th({
  children,
  alinhamento = 'esquerda',
  className,
}: {
  children?: React.ReactNode;
  alinhamento?: 'esquerda' | 'direita';
  className?: string;
}) {
  return (
    <th
      className={cn('tipo-rotulo p-4', alinhamento === 'direita' && 'text-right', className)}
      scope="col"
    >
      {children}
    </th>
  );
}

export function Tbody({ children }: { children: React.ReactNode }) {
  return <tbody>{children}</tbody>;
}

export function Tr({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const clicavel = !!onClick;
  return (
    <tr
      onClick={onClick}
      role={clicavel ? 'button' : undefined}
      tabIndex={clicavel ? 0 : undefined}
      onKeyDown={
        clicavel
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        'border-b border-border transition-colors duration-[var(--dur-fast)] last:border-0',
        // A linha ímpar recebe um véu. Cinquenta linhas separadas só por um fio de 7% de opacidade
        // eram uma parede de texto — o olho perdia a linha ao correr para a coluna da direita.
        'odd:bg-[color-mix(in_oklab,var(--surface-2)_45%,transparent)]',
        clicavel &&
          // O hover é ÂMBAR, não um cinza mais claro: é a cor da marca marcando onde você está.
          'cursor-pointer outline-none hover:bg-[color-mix(in_oklab,var(--primary)_11%,transparent)] focus-visible:bg-surface-hover focus-visible:shadow-[inset_var(--focus-ring)]',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function Td({
  children,
  mono = false,
  alinhamento = 'esquerda',
  className,
}: {
  children?: React.ReactNode;
  mono?: boolean;
  alinhamento?: 'esquerda' | 'direita';
  className?: string;
}) {
  return (
    <td
      className={cn(
        'px-4 py-3',
        mono ? 'tipo-dado text-text-secondary' : 'text-[15px] text-text',
        alinhamento === 'direita' && 'text-right',
        className,
      )}
    >
      {children}
    </td>
  );
}
